import {
  ACTOR_ID_HEADER_KEY,
  AUTHORIZATION_HEADER_KEY,
  COMMUNICATION_SERVICE_BASE_URL,
  PROFILE_SERVICE_BASE_URL,
} from "constants";
import { useState, useCallback } from "react";
import apiClient from "utils/apiClient";
import { useAuth } from "contexts/AuthContext";

const unwrapEmailList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.payload)) return payload.payload;
  if (Array.isArray(payload?.payload?.content)) return payload.payload.content;
  if (Array.isArray(payload?.enterpriseMessages)) return payload.enterpriseMessages;
  return [];
};

const toPlainText = (value) => {
  if (value == null) return "";
  const input = String(value);

  const withLineBreaks = input
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\s*li[^>]*>/gi, "- ")
    .replace(/<\/\s*(p|div|li|tr|h[1-6])\s*>/gi, "\n");

  let text = withLineBreaks;
  if (typeof DOMParser !== "undefined") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(withLineBreaks, "text/html");
    text = doc.body?.textContent || "";
  } else {
    text = withLineBreaks.replace(/<[^>]*>/g, "");
  }

  return text
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const sanitizeEmailHtml = (value) => {
  if (value == null) return "";
  const input = String(value);

  if (typeof DOMParser === "undefined" || typeof document === "undefined") {
    return input.replace(/<\s*\/?\s*div\b[^>]*>/gi, "");
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(input, "text/html");

  doc.querySelectorAll("script, style, iframe, object, embed, link, meta, base, form, input, button, textarea, select").forEach((node) => {
    node.remove();
  });

  doc.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value || "";
      if (name.startsWith("on")) {
        el.removeAttribute(attr.name);
      }
      if ((name === "href" || name === "src") && /^\s*javascript:/i.test(value)) {
        el.removeAttribute(attr.name);
      }
    });
  });

  doc.querySelectorAll("div").forEach((div) => {
    const p = doc.createElement("p");
    Array.from(div.attributes).forEach((attr) => {
      p.setAttribute(attr.name, attr.value);
    });
    while (div.firstChild) {
      p.appendChild(div.firstChild);
    }
    div.replaceWith(p);
  });

  doc.querySelectorAll("p").forEach((p) => {
    const text = (p.textContent || "").replace(/\u00a0/g, " ").trim();
    const hasMedia = p.querySelector("img, video, audio, iframe, table, hr, ul, ol");
    if (!text && !hasMedia) {
      p.remove();
    }
  });

  return (doc.body?.innerHTML || "").trim();
};

export const normalizeEmailMessage = (message) => {
  const base = message || {};
  const recipients = base.recipients ?? base.to ?? [];
  const cc = base.cc ?? [];
  const bcc = base.bcc ?? [];
  const rawSubject = base.subject ?? base.title ?? "";
  const rawTitle = base.title ?? rawSubject;
  const rawBody = base.body ?? base.content ?? "";
  const subject = toPlainText(rawSubject).replace(/\s+/g, " ");
  const title = toPlainText(rawTitle).replace(/\s+/g, " ");
  const body = toPlainText(rawBody);
  const bodyHtml = sanitizeEmailHtml(rawBody);
  const sentAt = base.sentAt ?? null;

  return {
    ...base,
    id: base.id ?? null,
    correlationId: base.correlationId,
    subject,
    title: title || subject,
    rawBody,
    body,
    bodyHtml,
    to: base.to ?? recipients,
    recipients,
    cc,
    bcc,
    sender: base.sender,
    status: base.status ?? base.emailStatus,
    emailStatus: base.emailStatus,
    direction: base.direction,
    attachments: base.attachments ?? [],
    sentAt,
  };
};


export default function useEmails() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const getRequestHeaders = useCallback(() => {
    const token = sessionStorage.getItem("authToken");
    return {
      [ACTOR_ID_HEADER_KEY]: user?.id || "",
      [AUTHORIZATION_HEADER_KEY]: token ? `Bearer ${token}` : "",
    };
  }, [user?.id]);

  // Get all users (for recipient selection)
  const getAllUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${PROFILE_SERVICE_BASE_URL}/api/v1/user-profiles/search`);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to fetch users";
      console.error("[useEmails:getAllUsers]", errorMessage);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Send email
  const sendEmail = useCallback(async (emailData) => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...emailData,
        title: emailData?.title || emailData?.subject || "",
        subject: emailData?.subject || emailData?.title || "",
      };

      const response = await apiClient.post(
        `${COMMUNICATION_SERVICE_BASE_URL}/api/v1/mailersend/send`,
        payload
      );
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to send email";
      console.error("[useEmails:sendEmail]", errorMessage);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);


  // Get sent emails (/sent)
  const getSentEmails = useCallback(async (page = 0, size = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `${COMMUNICATION_SERVICE_BASE_URL}/api/v1/mailersend/sent`,
        {
          headers: getRequestHeaders(),
          params: { page, size },
        }
      );
      const list = unwrapEmailList(response.data);
      console.log("[useEmails:getSentEmails] raw response:", response.data);
      console.log("[useEmails:getSentEmails] normalized count:", list.length);
      return list.map(normalizeEmailMessage);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to fetch sent emails";
      console.error("[useEmails:getSentEmails]", errorMessage);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getRequestHeaders]);

  // Get sent email by ID (/sent/{emailId})
  const getSentEmailById = useCallback(async (emailId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `${COMMUNICATION_SERVICE_BASE_URL}/api/v1/mailersend/sent/${emailId}`,
        {
          headers: getRequestHeaders(),
        }
      );
      return normalizeEmailMessage(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to fetch sent email";
      console.error("[useEmails:getSentEmailById]", errorMessage);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getRequestHeaders]);

  return {
    loading,
    error,
    getAllUsers,
    sendEmail,
    getSentEmails,
    getSentEmailById,
  };
}
